import { Texture } from "pixi.js";
import type { BootLaunchPayload } from "@slot/game-shell";
import type { Log } from "@slot/presentation";
import type { SpineArtUrls } from "@slot/presentation/rive-spine";
import { METER_FLAMES_LAYOUT } from "../scene/layout";
import type { MeterFlamesArt } from "../scene/MeterFlamesView";
import type { MysteryReelArt } from "../scene/MysteryReel";

/**
 * The bundle the game loads after it starts (`assets-src/game-deferred`): art it draws only later, so it costs nothing
 * before the first spin — the respin meter's flames while LIFE plays, the mystery jackpot's reel at the bonus's end.
 */
export const DEFERRED_BUNDLE = "game-deferred";

/**
 * The artist's animations as Spine skeletons (`assets-src/game-spine`: rive-spine's conversion of decision 66's Rive
 * files — the pots, their shine, the flying light, its impact, the activated pots' smoke, a pot's eruption, its fire
 * streaks), also fetched after the start. Never loaded as a Pixi bundle: each effect's skeleton, atlas and pages (its
 * folder's `skeleton.json`, `atlas.atlas`, `pages.pages`) are fetched by URL as it loads, the pages decoded as Rive
 * decodes them.
 */
const SPINE_BUNDLE = "game-spine";

/** Each converted file's folder in the Spine bundle. */
const SPINE_ART = {
  potionPots: "potion-pots",
  potShine: "pot-shine",
  edgeFire: "edge-fire",
  flyingLight: "flying-light",
  impact: "impact",
  potSmoke: "pot-smoke",
  potEruption: "pot-eruption",
  fireStreak: "fire-streak",
  boardImpact: "board-impact",
  cellHit: "cell-hit",
  cellFire: "cell-fire",
  collector: "collector",
  coinIdle: "coin-idle",
  bonusTransition: "bonus-transition",
} as const;

/**
 * Where one of the converted files is served — its `skeleton` (another of its artboards' over the same atlas: the edge
 * fire's `top` and `bottom`); null (logged) when the build lacks one of its files — what it would draw keeps its stand-in.
 */
export function spineArtUrls(input: { assets: BootLaunchPayload["assets"]; log: Log }, art: keyof typeof SPINE_ART, skeletonFile = "skeleton"): SpineArtUrls | null {
  const url = (file: string): string | null => input.assets.resolveUrl(SPINE_BUNDLE, `${SPINE_ART[art]}/${file}`);
  const [skeleton, atlas, pages] = [url(skeletonFile), url("atlas"), url("pages")];
  if (skeleton && atlas && pages) return { skeleton, atlas, pages };
  input.log(`[DeferredVisualAssets] no "${SPINE_BUNDLE}/${SPINE_ART[art]}" skeleton, atlas or pages in the manifest: its stand-in stays`);
  return null;
}

/** The deferred bundle's art. */
export interface DeferredVisualAssets {
  readonly meterFlames: MeterFlamesArt;
  /** The mystery jackpot's reel pieces; null (logged) when one is missing: a mystery jackpot then turns over as a mystery coin does. */
  readonly mysteryReel: MysteryReelArt | null;
}

/** The mystery reel's pieces in the deferred bundle (`mystery-reel/`, as the art import names them). */
const MYSTERY_REEL_PIECES = "mystery-reel";

/** Loads the deferred bundle: its art, or null (logged) when it is missing or fails — nothing then shows of it. */
export async function loadDeferredVisualAssets(input: { assets: BootLaunchPayload["assets"]; log: Log }): Promise<DeferredVisualAssets | null> {
  const { assets, log } = input;
  if (!assets.hasBundle(DEFERRED_BUNDLE)) {
    log(`[DeferredVisualAssets] no "${DEFERRED_BUNDLE}" bundle in the manifest`);
    return null;
  }
  try {
    await assets.loadBundle(DEFERRED_BUNDLE);
  } catch (error) {
    log(`[DeferredVisualAssets] "${DEFERRED_BUNDLE}" not loaded: ${error instanceof Error ? error.message : String(error)}`);
    return null;
  }
  // Each image whole: the asset build frames an image with transparent margins to its opaque part, and a flame's part
  // moves from frame to frame; the source behind it is the whole image.
  const whole = (path: string): Texture | null => {
    try {
      return new Texture({ source: assets.getLoadedAssetOrThrow<Texture>(DEFERRED_BUNDLE, path).source });
    } catch {
      log(`[DeferredVisualAssets] texture "${path}" not loaded`);
      return null;
    }
  };
  const frames = Array.from({ length: METER_FLAMES_LAYOUT.frames }, (_, i) => whole(`meter-flames/fire_${String(i).padStart(2, "0")}`));
  if (frames.some((frame) => frame === null)) return null;
  const piece = (name: string): Texture | null => whole(`${MYSTERY_REEL_PIECES}/${name}`);
  const reel = {
    frame: piece("frame"),
    shade: piece("shade"),
    mask: piece("mask"),
    glow: piece("glow_back"),
    text: piece("text_mystery_jackpot_equal"),
    bands: { mystery: piece("band_mystery"), mini: piece("band_mini"), minor: piece("band_minor"), major: piece("band_major") },
    winFrame: piece("win_frame"),
    winShine: piece("win_shine"),
    sparkle: piece("sparkle"),
  };
  const { bands, ...rest } = reel;
  const complete = [...Object.values(rest), ...Object.values(bands)].every((texture) => texture !== null);
  return {
    meterFlames: { frames: frames as Texture[], glow: whole("meter-flames/glow"), spark: whole("meter-flames/spark") },
    mysteryReel: complete ? (reel as MysteryReelArt) : null,
  };
}
